/**
 * BS-2151: React hook to instrument an OLA form step.
 *
 * Wires the canonical lifecycle events for a single step:
 *
 *   - `step_view`: emitted once on mount.
 *   - `validation_error`: emitted as new RHF validation errors
 *     appear, with sanitized metadata (length, has_*).
 *
 * `step_complete` is emitted explicitly by each step page's
 * `onSubmit` (after the page decides the submission is happy-path),
 * not by this hook. The hook can't tell from `formState.isSubmitting`
 * alone whether the resolver+API call succeeded.
 *
 * `step_abandon` is emitted by the global unload listener installed
 * in `client.ts` — the hook doesn't need to wire it per-step.
 *
 * # Usage
 *
 * ```ts
 * useStepAnalytics({
 *   step: 'personal-info',
 *   formState: form.formState,
 *   loanType: '0',  // optional, for funnel split-by-product
 * });
 * ```
 *
 * # Why not emit step_complete here?
 *
 * The hook would need to observe the submission *result* (resolver
 * pass + API success), not just `formState.isSubmitted`. RHF marks
 * `isSubmitted = true` on validation failure too. Putting the emit
 * in `onSubmit` (which only runs after RHF accepts the values) is
 * the unambiguous happy-path signal.
 *
 * # Field focus / blur
 *
 * Not instrumented in v1. The RHF field-wrapper components in this
 * codebase (`FormField`, `Input`, etc.) don't expose `onFocus`/
 * `onBlur` props in a uniform way without changes to the bedrock UI
 * library — out of scope for BS-2151. The acceptance criteria call
 * for these events but the cost is non-trivial across the ~30 fields
 * in OLA; defer to a follow-up if PSA actually wants this signal.
 * The event types are reserved in the schema so we can backfill
 * without a migration.
 */

'use client';

import { useEffect, useRef } from 'react';
import type { FieldErrors, FieldValues, FormState } from 'react-hook-form';
import type { OnlineApplicationStep } from '@multiplicu/contracts/los';

import { emitEvent } from './client';

type UseStepAnalyticsOptions<TFieldValues extends FieldValues = FieldValues> = {
  /** The canonical step name (mirrors the contract enum). */
  step: OnlineApplicationStep;
  /**
   * RHF formState. We only read `errors` + the field paths within;
   * passing the full object lets the hook re-run when errors change
   * without callers needing to memoize a slice.
   */
  formState?: Pick<FormState<TFieldValues>, 'errors'>;
  /**
   * Selected loan type (for funnel split-by-product). Optional —
   * pre-loan-selection steps emit without it.
   */
  loanType?: string;
};

/**
 * Diff two RHF error maps and return the new error entries that
 * weren't present (or had a different error_code) in the previous
 * map. Returns a flat array of [field_name, error_code] tuples
 * because that's the shape the emit call needs.
 *
 * We compare by error message + type because RHF's error object
 * is mutable; same identity doesn't mean same content. The `type`
 * field is the Zod-side error code (e.g. `too_small`, `invalid_type`)
 * which is exactly what BS-2150's `error_code` column wants.
 */
function diffErrors(
  prev: FieldErrors,
  next: FieldErrors
): Array<{ field_name: string; error_code: string }> {
  const newErrors: Array<{ field_name: string; error_code: string }> = [];
  for (const [field, error] of Object.entries(next)) {
    if (!error || typeof error !== 'object') continue;
    // RHF errors may be nested (e.g. for arrays / nested objects).
    // We only emit top-level field errors in v1 to keep the volume
    // sane and the field_name shape consistent.
    const errorWithType = error as { type?: string; message?: string };
    const code = errorWithType.type ?? 'unknown';
    const prevEntry = prev[field] as { type?: string } | undefined;
    if (prevEntry?.type === code) continue; // unchanged
    newErrors.push({ field_name: field, error_code: code });
  }
  return newErrors;
}

/**
 * Instrument a form step. Call once near the top of the step's
 * component body.
 */
export function useStepAnalytics<TFieldValues extends FieldValues = FieldValues>(
  options: UseStepAnalyticsOptions<TFieldValues>
): void {
  const { step, formState, loanType } = options;

  // step_view: fire once on mount. The empty dep array is intentional
  // — re-renders within the same step should NOT re-emit. If a member
  // navigates away and back, the route remount drives a fresh
  // step_view.
  useEffect(() => {
    emitEvent('step_view', step, loanType ? { loan_type: loanType } : {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // validation_error: diff against the prior errors map, emit one
  // event per newly-introduced error. Storing the previous map in a
  // ref keeps re-render counts down and avoids stale-closure traps.
  const prevErrorsRef = useRef<FieldErrors>({});

  useEffect(() => {
    if (!formState?.errors) return;
    const newErrors = diffErrors(prevErrorsRef.current, formState.errors);
    for (const err of newErrors) {
      // We deliberately do NOT pass the raw field value here. The
      // validation_error event records the field NAME and the Zod
      // error CODE; the sanitized error_metadata (length, has_*) is
      // only added when the caller explicitly computes it. The hook
      // doesn't have access to raw values (formState.errors carries
      // only the error descriptor, not the offending value), so
      // there's no path by which a raw value could leak from this
      // emit site.
      emitEvent('validation_error', step, {
        field_name: err.field_name,
        error_code: err.error_code,
        ...(loanType ? { loan_type: loanType } : {}),
      });
    }
    prevErrorsRef.current = formState.errors;
  }, [formState?.errors, step, loanType]);
}
